import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Card, CardActionArea, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, useColorScheme, useTheme } from '@mui/material/styles';

import ApexCharts, { ApexOptions } from 'apexcharts';

/**
 * React.StrictMode（開発時）では passive effect が mount→unmount→mount と二重実行される。
 * react-apexcharts はマウント effect で ApexCharts を生成し render()（非同期）するが、
 * StrictMode の擬似 unmount で destroy() が先に走ると内部 DOM 未生成のまま破棄され
 * "Cannot read properties of undefined (reading 'node' / 'beforeMount')" が発生する。
 * react-apexcharts の cleanup はこの競合をガードしないため、遅延マウント程度では回避できない。
 *
 * 対策: react-apexcharts を使わず apexcharts を直接命令的に扱う。
 * - render() の完了を Promise で追跡し、未完了のまま destroy() しない
 * - unmount 時は必ず try/catch で保護し、破棄後は ref を null にする（二重破棄を防ぐ）
 * - options/series の変更は updateOptions / updateSeries で反映（再生成しない）
 */
type SafeApexChartProps = {
  options: ApexOptions;
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  type: 'donut' | 'bar' | 'rangeBar' | 'line' | 'area' | 'pie';
  height?: number | string;
  width?: number | string;
};
export const SafeApexChart: React.FC<SafeApexChartProps> = ({
  options,
  series,
  type,
  height,
  width,
}) => {
  // ApexCharts をマウントするコンテナ（この直下に「インスタンスごとの子要素」を作る）
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  // options/series を最新参照で保持（effect を作り直さないため）
  const propsRef = React.useRef({ options, series, type, height, width });
  propsRef.current = { options, series, type, height, width };
  const handleRef = React.useRef<{ chart: ApexCharts; rendered: boolean } | null>(null);

  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // StrictMode の二重マウントや連続再マウントで、前インスタンスの遅延 destroy が
    // 後インスタンスの DOM を巻き込んで消してしまう問題を避けるため、
    // インスタンスごとに専用の子要素を作り、その中だけを ApexCharts に描画・破棄させる。
    const mountEl = document.createElement('div');
    host.appendChild(mountEl);

    const p = propsRef.current;
    let chart: ApexCharts;
    try {
      chart = new ApexCharts(mountEl, {
        ...p.options,
        chart: { ...(p.options.chart ?? {}), type: p.type, height: p.height, width: p.width },
        series: p.series,
      });
    } catch {
      host.removeChild(mountEl);
      return;
    }

    const handle = { chart, rendered: false };
    handleRef.current = handle;

    // render() の完了を追跡（未完了で destroy すると 'node' 参照エラーになるため）
    const renderPromise = chart
      .render()
      .then(() => {
        handle.rendered = true;
      })
      .catch(() => {
        handle.rendered = false;
      });

    return () => {
      if (handleRef.current === handle) handleRef.current = null;
      // 専用の子要素ごと確実に片付ける。render 完了を待ってから destroy する。
      const finish = () => {
        try {
          chart.destroy();
        } catch {
          /* noop */
        }
        try {
          if (mountEl.parentNode === host) host.removeChild(mountEl);
        } catch {
          /* noop */
        }
      };
      if (handle.rendered) {
        finish();
      } else {
        renderPromise.finally(finish);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // options / series / サイズ変更の反映（インスタンスは再生成しない）
  React.useEffect(() => {
    const handle = handleRef.current;
    if (!handle || !handle.rendered) return;
    try {
      handle.chart.updateOptions(
        { ...options, chart: { ...(options.chart ?? {}), type, height, width } },
        false,
        false,
      );
      handle.chart.updateSeries(series, false);
    } catch {
      /* noop */
    }
  }, [options, series, type, height, width]);

  return <Box ref={hostRef} sx={{ width: width ?? '100%' }} />;
};

/**
 * cssVariables + colorSchemes を有効にしているため、useTheme().palette.mode /
 * palette.text.* は「アクティブなカラースキーム」を反映しない（CSS変数文字列 or 既定モード）。
 * ApexCharts は具体的な色値と mode を必要とするため、useColorScheme() で実際のモードを取得し、
 * ラベル/軸/グリッドの色は具体値（hex）で解決する。これをしないとダーク時にラベルが背景に埋もれる。
 */
const useChartColors = () => {
  const { mode, systemMode } = useColorScheme();
  const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
  return {
    isDark,
    mode: (isDark ? 'dark' : 'light') as 'dark' | 'light',
    // 常に読める具体色（テーマトークンの実値に対応）
    textPrimary: isDark ? '#e6e8ee' : '#1a1d24',
    textSecondary: isDark ? '#a2a8b5' : '#5b616e',
    divider: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
  };
};

/**
 * ドメインダッシュボード共通のウィジェット部品。
 * 研修 / 研修管理トップのローカル実装を共通化したもの。
 * デザインルール（角丸2〜3, elevation=0 + divider ボーダー）に準拠。
 */

// --- ウィジェットの外枠 ---
export const Widget: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  /**
   * 高さの扱い。既定 'stretch' は行内で最も高いウィジェットに合わせて伸びる（KPI/リスト向け）。
   * 'content' は自身のコンテンツ高さに収め、隣のウィジェットに引き摺られて伸びない（グラフ向け）。
   */
  fit?: 'stretch' | 'content';
}> = ({ title, action, children, fit = 'stretch' }) => (
  <Card
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 3,
      p: 2.5,
      // 'content' のときは行の高さに引き摺られないよう、自身のコンテンツ高さに収める
      height: fit === 'content' ? 'auto' : '100%',
      alignSelf: fit === 'content' ? 'flex-start' : 'stretch',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {action}
    </Stack>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Card>
);

// --- コンパクトな KPI タイル（クリックで遷移可能） ---
export const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  suffix: string;
  color: string;
  loading: boolean;
  onClick?: () => void;
}> = ({ icon, label, value, suffix, color, loading, onClick }) => {
  const clickable = !!onClick;
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
        p: 1.75,
        height: '100%',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(clickable && {
          '&:hover': {
            borderColor: color,
            transform: 'translateY(-2px)',
            '& .stat-arrow': { opacity: 1, transform: 'translateX(0)' },
          },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Box
          sx={(t) => ({
            width: 30,
            height: 30,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
            ...t.applyStyles('dark', { bgcolor: alpha(color, 0.24) }),
          })}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          noWrap
          sx={{ flex: 1 }}
        >
          {label}
        </Typography>
        {clickable && (
          <ArrowForwardIcon
            className="stat-arrow"
            sx={{
              fontSize: 15,
              color,
              opacity: 0,
              transform: 'translateX(-4px)',
              transition: 'opacity 0.2s, transform 0.2s',
              flexShrink: 0,
            }}
          />
        )}
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={56} height={32} />
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={0.5}>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {suffix}
          </Typography>
        </Stack>
      )}
    </Card>
  );
};

// --- 内訳付き KPI タイル（合計 + サブカウントを縦に表示） ---
export const StatBreakdownTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  /** 合計値 */
  total: number;
  suffix?: string;
  /** 内訳（申請中/対応中など）。クリックで個別遷移も可能 */
  breakdown: { label: string; value: number; onClick?: () => void }[];
  color: string;
  loading: boolean;
  /** ヘッダー右上のアクション（カンバンリンク等）。 */
  action?: React.ReactNode;
  /** 合計値クリック時の遷移（省略時は非クリック）。 */
  onClick?: () => void;
  /**
   * 内訳を省スペースなチップ行（折り返し）で表示する。
   * 内訳項目が多い場合に縦長になりすぎないようにする（既定は縦積み）。
   */
  compact?: boolean;
}> = ({
  icon,
  label,
  total,
  suffix = '件',
  breakdown,
  color,
  loading,
  action,
  onClick,
  compact = false,
}) => (
  <Card
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 3,
      p: 1.75,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
      <Box
        sx={(t) => ({
          width: 30,
          height: 30,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
          flexShrink: 0,
          ...t.applyStyles('dark', { bgcolor: alpha(color, 0.24) }),
        })}
      >
        {icon}
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap sx={{ flex: 1 }}>
        {label}
      </Typography>
      {action}
    </Stack>

    {loading ? (
      <Skeleton variant="text" width={56} height={32} />
    ) : (
      <>
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={0.5}
          onClick={onClick}
          sx={{
            cursor: onClick ? 'pointer' : 'default',
            borderRadius: 1,
            width: 'fit-content',
            ...(onClick && { '&:hover': { opacity: 0.75 } }),
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
            {total}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {suffix}
          </Typography>
        </Stack>
        {compact ? (
          // 省スペース: 「ラベル 値」の小チップを折り返しで並べる
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {breakdown.map((b) => (
              <Stack
                key={b.label}
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={b.onClick}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.125,
                  cursor: b.onClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s',
                  ...(b.onClick && { '&:hover': { bgcolor: 'action.hover' } }),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {b.label}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color }}>
                  {b.value}
                </Typography>
              </Stack>
            ))}
          </Box>
        ) : (
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            {breakdown.map((b) => (
              <Stack
                key={b.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={b.onClick}
                sx={{
                  cursor: b.onClick ? 'pointer' : 'default',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  transition: 'background-color 0.15s',
                  ...(b.onClick && { '&:hover': { bgcolor: 'action.hover' } }),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {b.label}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ color }}>
                  {b.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </>
    )}
  </Card>
);

// --- 機能ナビゲーションカード（各画面への遷移導線） ---
export const NavCard: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  /** 省略時は説明行を表示しない */
  description?: string;
  color: string;
}> = ({ onClick, icon, title, description, color }) => (
  <Card
    elevation={0}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 3,
      height: '100%',
      transition: 'all 0.2s ease-in-out',
      '&:hover': { borderColor: color, transform: 'translateY(-2px)' },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ height: '100%', p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={(t) => ({
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
            ...t.applyStyles('dark', { bgcolor: alpha(color, 0.24) }),
          })}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Stack>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardActionArea>
  </Card>
);

// --- 空状態のヒント ---
export const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

// --- リスト行（ウィジェット内のクリック可能な1行） ---
export const WidgetRow: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  accentColor?: string;
}> = ({ onClick, children, accentColor = 'primary.main' }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    onClick={onClick}
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      px: 1.5,
      py: 0.75,
      cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { borderColor: accentColor, bgcolor: 'action.hover' },
    }}
  >
    {children}
  </Stack>
);

// --- 画面内ページング付きリスト（ウィジェット内で縦に伸びないようにする） ---
/**
 * ウィジェット内のリストを固定件数ずつ表示し、縦に伸びないようにする。
 * ページャーは「表示レンジ / 総件数」＋左右矢印のミニマルなUI。
 * 末尾で「次へ」を押すと先頭へ、先頭で「前へ」を押すと末尾へループする。
 * 1ページに収まる場合はページャーを表示しない（従来の見た目を維持）。
 * APIには絡まない、クライアント側だけのページングである。
 */
export function PagedWidgetList<T>({
  items,
  renderItem,
  pageSize = 6,
  keyOf,
  spacing = 1,
  page: controlledPage,
  onPageChange,
}: {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** 1ページの表示件数（既定6） */
  pageSize?: number;
  /** 各要素の React key（省略時は index） */
  keyOf?: (item: T, index: number) => React.Key;
  /** 行間スペーシング（既定1） */
  spacing?: number;
  /**
   * 現在ページ（0始まり・任意）。省略時は内部stateで管理する（従来動作）。
   * user_profile 復元でページ状態を外部から制御したい場合に onPageChange と併せて渡す。
   */
  page?: number;
  /** ページ変更コールバック（任意）。controlled 利用時に指定 */
  onPageChange?: (page: number) => void;
}) {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const [internalPage, setInternalPage] = React.useState(0);
  // controlled（page/onPageChange 指定）なら外部 state を、無ければ内部 state を使う
  const isControlled = controlledPage != null && onPageChange != null;
  const page = isControlled ? (controlledPage as number) : internalPage;
  const setPage = React.useCallback(
    (updater: number | ((p: number) => number)) => {
      const next = typeof updater === 'function' ? updater(page) : updater;
      if (isControlled) {
        onPageChange?.(next);
      } else {
        setInternalPage(next);
      }
    },
    [isControlled, onPageChange, page],
  );
  // スライド方向（'next' = 左へ流れて次が右から入る / 'prev' = その逆）と一回限りのアニメーションキー
  const [direction, setDirection] = React.useState<'next' | 'prev'>('next');
  const [animKey, setAnimKey] = React.useState(0);

  // 件数変化でページ数が減ったときのクランプ
  React.useEffect(() => {
    setPage((p) => Math.min(p, pageCount - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  const start = page * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  const showPager = total > pageSize;

  const goPrev = () => {
    setDirection('prev');
    setAnimKey((k) => k + 1);
    setPage((p) => (p - 1 + pageCount) % pageCount);
  };
  const goNext = () => {
    setDirection('next');
    setAnimKey((k) => k + 1);
    setPage((p) => (p + 1) % pageCount);
  };

  return (
    <Stack sx={{ height: '100%' }}>
      {/* スライド用のクリッピングコンテナ */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Stack
          // animKey を変えることで都度アニメーションを再生する
          key={animKey}
          spacing={spacing}
          sx={{
            '@keyframes slideFromRight': {
              from: { transform: 'translateX(24px)', opacity: 0 },
              to: { transform: 'translateX(0)', opacity: 1 },
            },
            '@keyframes slideFromLeft': {
              from: { transform: 'translateX(-24px)', opacity: 0 },
              to: { transform: 'translateX(0)', opacity: 1 },
            },
            animation: `${direction === 'next' ? 'slideFromRight' : 'slideFromLeft'} 0.25s ease`,
          }}
        >
          {pageItems.map((item, i) => (
            <React.Fragment key={keyOf ? keyOf(item, start + i) : start + i}>
              {renderItem(item, start + i)}
            </React.Fragment>
          ))}
        </Stack>
      </Box>

      {showPager && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ mt: 1.25, pt: 1, borderTop: 1, borderColor: 'divider' }}
        >
          <IconButton size="small" onClick={goPrev} aria-label="前のページ">
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 72, textAlign: 'center' }}
          >
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
              {start + 1}–{Math.min(start + pageSize, total)}
            </Box>
            {' / '}
            {total}
          </Typography>
          <IconButton size="small" onClick={goNext} aria-label="次のページ">
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}

// --- 「もっと見る / 折りたたむ」トグル（棒グラフ共通） ---
const ShowMoreToggle: React.FC<{
  expanded: boolean;
  hiddenCount: number;
  onToggle: () => void;
}> = ({ expanded, hiddenCount, onToggle }) => (
  <Box sx={{ textAlign: 'center', mt: 0.5 }}>
    <Typography
      component="button"
      onClick={onToggle}
      variant="caption"
      sx={{
        cursor: 'pointer',
        border: 0,
        bgcolor: 'transparent',
        color: 'primary.main',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        py: 0.25,
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {expanded ? '折りたたむ' : `もっと見る（残り${hiddenCount}件）`}
    </Typography>
  </Box>
);

// --- ウィジェット内の横棒グラフ（集計の可視化） ---
export type BarDatum = { label: string; value: number };

/**
 * ウィジェット内に収める横棒グラフ。
 * 集計データ（label/value）を降順の横棒で表示する。
 * onBarClick を渡すとバー（またはラベル）クリックで絞り込み遷移などに使える。
 * 空データ時は EmptyHint を表示する。
 */
export const WidgetBarChart: React.FC<{
  data: BarDatum[];
  color: string;
  /** 折りたたみ時に表示する件数（既定 8）。これを超える分は「もっと見る」で展開 */
  collapsedItems?: number;
  /** バークリック時（ラベル文字列を渡す） */
  onBarClick?: (label: string) => void;
  /** 空データ時の文言 */
  emptyText?: string;
  height?: number;
}> = ({
  data,
  color,
  collapsedItems = 8,
  onBarClick,
  emptyText = 'データがありません',
  height,
}) => {
  const theme = useTheme();
  const c = useChartColors();
  const [expanded, setExpanded] = React.useState(false);

  const allSorted = React.useMemo(() => [...data].sort((a, b) => b.value - a.value), [data]);
  if (allSorted.length === 0) {
    return <EmptyHint text={emptyText} />;
  }

  const canExpand = allSorted.length > collapsedItems;
  const sorted = expanded ? allSorted : allSorted.slice(0, collapsedItems);

  const categories = sorted.map((d) => d.label);
  const values = sorted.map((d) => d.value);
  const chartHeight = height ?? Math.max(120, sorted.length * 34 + 16);

  const options: ApexOptions = {
    // useColorScheme() で解決した実モードを渡し、軸/ラベル/凡例の既定文字色を追従させる
    theme: { mode: c.mode },
    chart: {
      type: 'bar',
      background: 'rgba(0,0,0,0)',
      toolbar: { show: false },
      fontFamily: theme.typography.fontFamily,
      // 控えめな伸長アニメーション
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: { enabled: true, delay: 60 },
        dynamicAnimation: { enabled: true, speed: 300 },
      },
      events: onBarClick
        ? {
            dataPointSelection: (_e, _ctx, cfg) => {
              const label = categories[cfg.dataPointIndex];
              if (label) onBarClick(label);
            },
          }
        : undefined,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '62%',
        distributed: false,
        // 値ラベルはバーの端（外側）に表示する
        dataLabels: { position: 'top' },
      },
    },
    colors: [color],
    dataLabels: {
      enabled: true,
      // バー外側に表示するため、具体色の主テキスト色で常に読めるようにする
      style: { fontSize: '11px', fontWeight: 700, colors: [c.textPrimary] },
      offsetX: 16,
      background: { enabled: false },
      formatter: (v: number) => `${v}`,
    },
    grid: { borderColor: c.divider, strokeDashArray: 3 },
    xaxis: {
      categories,
      labels: { style: { colors: c.textSecondary, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: c.textPrimary, fontSize: '11px' } },
    },
    tooltip: { theme: c.mode },
    states: { active: { filter: { type: 'none' } } },
  };

  return (
    <Box sx={{ '& .apexcharts-bar-area': { cursor: onBarClick ? 'pointer' : 'default' } }}>
      <SafeApexChart
        options={options}
        series={[{ name: '件数', data: values }]}
        type="bar"
        height={chartHeight}
      />
      {canExpand && (
        <ShowMoreToggle
          expanded={expanded}
          hiddenCount={allSorted.length - collapsedItems}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </Box>
  );
};

// --- ドーナツ（内訳）チャート ---
export type DonutDatum = { label: string; value: number; color?: string };

/**
 * ウィジェット内に収めるドーナツチャート（構成比の可視化）。
 * 研修ドメインのドーナツと同じ見た目に統一し、tooltip/凡例/ラベルの色は
 * useChartColors() の実モードに追従する（ダーク/ライトで背景に埋もれない）。
 */
export const WidgetDonutChart: React.FC<{
  data: DonutDatum[];
  /** 中央に表示する合計ラベル（例: '要員'）。未指定なら合計値のみ */
  totalLabel?: string;
  /** 値のツールチップ単位（既定 '件'） */
  unit?: string;
  /** セグメントの既定カラーパレット（datum.color が無い場合に使用） */
  palette?: string[];
  emptyText?: string;
  height?: number;
  onSliceClick?: (label: string) => void;
}> = ({
  data,
  totalLabel,
  unit = '件',
  palette,
  emptyText = 'データがありません',
  height = 260,
  onSliceClick,
}) => {
  const theme = useTheme();
  const c = useChartColors();

  const filtered = data.filter((d) => d.value > 0);
  if (filtered.length === 0) {
    return <EmptyHint text={emptyText} />;
  }

  const labels = filtered.map((d) => d.label);
  const series = filtered.map((d) => d.value);
  const total = series.reduce((a, b) => a + b, 0);
  const defaultPalette = palette ?? [
    '#1565c0',
    '#00838f',
    '#6a1b9a',
    '#2e7d32',
    '#ef6c00',
    '#c62828',
    '#5c6bc0',
    '#9e9e9e',
  ];
  const colors = filtered.map((d, i) => d.color ?? defaultPalette[i % defaultPalette.length]);

  const options: ApexOptions = {
    theme: { mode: c.mode },
    chart: {
      type: 'donut',
      background: 'rgba(0,0,0,0)',
      fontFamily: theme.typography.fontFamily,
      animations: { enabled: true, speed: 600, animateGradually: { enabled: true, delay: 120 } },
      events: onSliceClick
        ? {
            dataPointSelection: (_e, _ctx, cfg) => {
              const label = labels[cfg.dataPointIndex];
              if (label) onSliceClick(label);
            },
          }
        : undefined,
    },
    labels,
    colors,
    legend: {
      position: 'bottom',
      markers: { size: 6 },
      itemMargin: { horizontal: 8 },
      labels: { colors: c.textSecondary },
    },
    dataLabels: { enabled: false },
    stroke: { width: 2, colors: [c.isDark ? '#0b0e18' : '#ffffff'] },
    plotOptions: {
      pie: {
        expandOnClick: false,
        donut: {
          size: '68%',
          labels: {
            show: true,
            value: { color: c.textPrimary },
            total: {
              show: true,
              label: totalLabel ?? '合計',
              color: c.textSecondary,
              formatter: () => `${total}`,
            },
          },
        },
      },
    },
    states: { hover: { filter: { type: 'lighten' } } },
    tooltip: { theme: c.mode, y: { formatter: (v: number) => `${v} ${unit}` } },
  };

  return (
    <Box sx={{ '& .apexcharts-pie-area': { cursor: onSliceClick ? 'pointer' : 'default' } }}>
      <SafeApexChart options={options} series={series} type="donut" height={height} />
    </Box>
  );
};

// --- 積み上げ横棒（グループ内訳）チャート ---
export type StackedSeries = { name: string; color?: string; data: number[] };

/**
 * カテゴリ（categories）ごとに複数系列を積み上げる横棒グラフ。
 * 例: パートナー別 × 雇用形態別の要員数など。
 * 色はテーマ実モードに追従し、軸/凡例ラベルが常に読める。
 */
export const WidgetStackedBarChart: React.FC<{
  categories: string[];
  series: StackedSeries[];
  unit?: string;
  emptyText?: string;
  height?: number;
  /** 折りたたみ時に表示する件数（既定 8）。これを超える分は「もっと見る」で展開 */
  collapsedItems?: number;
  /** バー（カテゴリ）クリック時。カテゴリラベルを渡す */
  onBarClick?: (label: string) => void;
}> = ({
  categories,
  series,
  unit = '件',
  emptyText = 'データがありません',
  height,
  collapsedItems = 8,
  onBarClick,
}) => {
  const theme = useTheme();
  const c = useChartColors();
  const [expanded, setExpanded] = React.useState(false);

  const hasData = series.some((s) => s.data.some((v) => v > 0));
  if (categories.length === 0 || !hasData) {
    return <EmptyHint text={emptyText} />;
  }

  // 折りたたみ時は先頭 collapsedItems 件だけ（categories は合計降順で渡される前提）
  const canExpand = categories.length > collapsedItems;
  const visCount = expanded ? categories.length : Math.min(collapsedItems, categories.length);
  const visCategories = categories.slice(0, visCount);
  const visSeries = series.map((s) => ({ ...s, data: s.data.slice(0, visCount) }));

  // WidgetBarChart と同じ高さ・バー体裁に揃える
  const chartHeight = height ?? Math.max(120, visCategories.length * 34 + 16);
  const colors = visSeries.map((s) => s.color).filter(Boolean) as string[];

  const options: ApexOptions = {
    theme: { mode: c.mode },
    chart: {
      type: 'bar',
      stacked: true,
      background: 'rgba(0,0,0,0)',
      toolbar: { show: false },
      fontFamily: theme.typography.fontFamily,
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: { enabled: true, delay: 60 },
        dynamicAnimation: { enabled: true, speed: 300 },
      },
      events: onBarClick
        ? {
            dataPointSelection: (_e, _ctx, cfg) => {
              const label = visCategories[cfg.dataPointIndex];
              if (label) onBarClick(label);
            },
          }
        : undefined,
    },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '62%' } },
    ...(colors.length === visSeries.length ? { colors } : {}),
    dataLabels: { enabled: false },
    grid: { borderColor: c.divider, strokeDashArray: 3 },
    xaxis: {
      categories: visCategories,
      labels: { style: { colors: c.textSecondary, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: c.textPrimary, fontSize: '11px' } } },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: { size: 6 },
      itemMargin: { horizontal: 8 },
      labels: { colors: c.textSecondary },
    },
    tooltip: { theme: c.mode, y: { formatter: (v: number) => `${v} ${unit}` } },
    states: { active: { filter: { type: 'none' } } },
  };

  return (
    <Box sx={{ '& .apexcharts-bar-area': { cursor: onBarClick ? 'pointer' : 'default' } }}>
      <SafeApexChart options={options} series={visSeries} type="bar" height={chartHeight} />
      {canExpand && (
        <ShowMoreToggle
          expanded={expanded}
          hiddenCount={categories.length - collapsedItems}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </Box>
  );
};

// --- ガントチャート（期間タイムライン） ---
export type GanttTask = {
  label: string;
  start: number; // epoch ms
  end: number; // epoch ms
  color?: string;
};

/**
 * rangeBar を用いた簡易ガントチャート。
 * 稼働中プロジェクトの開始〜終了（または現在）を横棒のタイムラインで表示する。
 * 色/軸ラベルはテーマ実モードに追従する。
 */
export const WidgetGanttChart: React.FC<{
  tasks: GanttTask[];
  emptyText?: string;
  height?: number;
  /** 折りたたみ時に表示する件数（既定 8）。これを超える分は「もっと見る」で展開 */
  collapsedItems?: number;
  onBarClick?: (label: string) => void;
}> = ({ tasks, emptyText = 'データがありません', height, collapsedItems = 8, onBarClick }) => {
  const theme = useTheme();
  const c = useChartColors();
  const [expanded, setExpanded] = React.useState(false);

  if (tasks.length === 0) {
    return <EmptyHint text={emptyText} />;
  }

  const canExpand = tasks.length > collapsedItems;
  const visTasks = expanded ? tasks : tasks.slice(0, collapsedItems);

  // ガントらしく行の高さは少し広めに確保（タスク名 ＋ 期間ラベルを載せる）
  const ROW_H = 40;
  const chartHeight = height ?? visTasks.length * ROW_H + 56;
  const fmtDate = (ms: number) => {
    const d = new Date(ms);
    return `${d.getFullYear()}/${`${d.getMonth() + 1}`.padStart(2, '0')}/${`${d.getDate()}`.padStart(2, '0')}`;
  };
  const data = visTasks.map((t) => ({
    x: t.label,
    y: [t.start, t.end],
    fillColor: t.color,
  }));

  // 軸範囲: 左端 = 表示プロジェクトの最も早い開始日（その月の初日）、右端 = 今月末
  const now = new Date();
  const minD = new Date(Math.min(...visTasks.map((t) => t.start)));
  const axisMin = new Date(minD.getFullYear(), minD.getMonth(), 1).getTime();
  const axisMax = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
  // 期間に応じた月数（縦の区切り列の本数）
  const monthSpan = Math.max(
    1,
    (new Date(axisMax).getFullYear() - new Date(axisMin).getFullYear()) * 12 +
      (new Date(axisMax).getMonth() - new Date(axisMin).getMonth()),
  );

  const barColor = theme.palette.primary.main;

  const options: ApexOptions = {
    theme: { mode: c.mode },
    chart: {
      type: 'rangeBar',
      background: 'rgba(0,0,0,0)',
      toolbar: { show: false, autoSelected: 'pan' },
      zoom: { enabled: false },
      selection: { enabled: false },
      fontFamily: theme.typography.fontFamily,
      animations: { enabled: true, speed: 500, animateGradually: { enabled: true, delay: 60 } },
      events: onBarClick
        ? {
            dataPointSelection: (_e, _ctx, cfg) => {
              const label = visTasks[cfg.dataPointIndex]?.label;
              if (label) onBarClick(label);
            },
          }
        : undefined,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 3,
        barHeight: '55%',
        rangeBarGroupRows: false,
        dataLabels: { hideOverflowingLabels: false },
      },
    },
    // バー内にタスク名を載せる（ガント表の行ラベルのように）
    dataLabels: {
      enabled: true,
      formatter: (_val, opts) => visTasks[opts.dataPointIndex]?.label ?? '',
      textAnchor: 'start',
      offsetX: 8,
      style: { fontSize: '11px', fontWeight: 600, colors: ['#ffffff'] },
      background: { enabled: false },
    },
    // ガントらしい時間グリッド: 縦線（月境界）を見せ、行はゼブラで区切る
    grid: {
      borderColor: c.divider,
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      row: { colors: [c.isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', 'transparent'] },
      padding: { left: 8, right: 8 },
    },
    xaxis: {
      type: 'datetime',
      min: axisMin,
      max: axisMax,
      tickAmount: Math.min(monthSpan, 12),
      position: 'top',
      labels: {
        style: { colors: c.textSecondary, fontSize: '11px' },
        datetimeUTC: false,
        format: 'yyyy/MM',
        rotate: 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: true, color: c.divider },
    },
    yaxis: { labels: { show: false } },
    // 「今日」を示す縦線（ガント定番のマーカー）
    annotations: {
      xaxis: [
        {
          x: now.getTime(),
          borderColor: theme.palette.error.main,
          strokeDashArray: 3,
          label: {
            text: '今日',
            orientation: 'horizontal',
            position: 'top',
            style: {
              color: '#ffffff',
              background: theme.palette.error.main,
              fontSize: '10px',
              fontWeight: 700,
            },
          },
        },
      ],
    },
    colors: [barColor],
    tooltip: {
      theme: c.mode,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
        const t = visTasks[dataPointIndex];
        if (!t) return '';
        const days = Math.max(1, Math.round((t.end - t.start) / (24 * 60 * 60 * 1000)));
        return `<div style="padding:6px 10px;font-size:12px;">
          <div style="font-weight:700;margin-bottom:2px;">${t.label}</div>
          <div>${fmtDate(t.start)} 〜 ${fmtDate(t.end)}</div>
          <div style="opacity:0.7;margin-top:2px;">期間: ${days} 日</div>
        </div>`;
      },
    },
    states: { active: { filter: { type: 'none' } } },
  };

  return (
    <Box sx={{ '& .apexcharts-rangebar-area': { cursor: onBarClick ? 'pointer' : 'default' } }}>
      <SafeApexChart options={options} series={[{ data }]} type="rangeBar" height={chartHeight} />
      {canExpand && (
        <ShowMoreToggle
          expanded={expanded}
          hiddenCount={tasks.length - collapsedItems}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
    </Box>
  );
};
